import { useState } from 'react';
import {Star} from 'lucide-react';

interface ReviewCardProps {
  name: string;
  text: string;
}

export function ReviewCard({ name, text }: ReviewCardProps) {
  const [expanded, setExpanded] = useState(false);
  const maxLength = 130;
  const isLong = text.length > maxLength;

  const displayParagraphs = expanded 
    ? text 
    : text.slice(0, maxLength) + (isLong ? '...' : '');

  const getAvatarColor = (name: string) => {
    const colors = ['bg-blue-500', 'bg-red-500', 'bg-green-600', 'bg-orange-500', 'bg-purple-500', 'bg-teal-500', 'bg-pink-500'];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 flex flex-col h-full hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4 mb-4">
        <div className="relative flex-shrink-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-lg ${getAvatarColor(name)}`}>
            {name.charAt(0)}
          </div>
          <div className="absolute -bottom-1 -right-1 flex items-center justify-center w-5 h-5 bg-white rounded-full p-0.5 shadow-sm">
             <svg viewBox="0 0 24 24" className="w-full h-full">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
             </svg>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-roboto font-semibold text-gray-900 text-[15px] truncate">{name}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-gray-500 text-[13px]">a month ago</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-0.5 mb-2">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-[15px] h-[15px] fill-[#fbbc04] text-[#fbbc04]" />
        ))}
      </div>
      
      <div className="text-gray-700 text-[14px] leading-relaxed flex-grow">
        <span className="whitespace-pre-line">{displayParagraphs}</span>
        {isLong && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-gray-500 font-medium hover:text-gray-700 block mt-1 transition-colors"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
    </div>
  );
}